var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Checkbox, Grid, Tooltip, } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { axiosClient } from '../axios';
import { UserContext, getAuthHeader } from '../auth';
import { MULTILANGUAGE, LOG_LEVELS, SERVICES, ENDPOINTS, } from '../constants';
import { infobaseTableSGStyles } from './infobaseTableSGStyles';
/**
 * Infobase Table SG
 * @param props Props
 * @returns JSX Element
 */
export function InfobaseTableSG(props) {
    const { selectedFiles, setSelectedFiles, reloadInfobaseFilesFlag, attachFileTypes, } = props;
    const { t, i18n } = useTranslation();
    const /** User credential */ user = useContext(UserContext);
    /**
     * Handle checkbox change
     * @param event Event
     * @param file File
     */
    const handleCheckboxChange = (event, file) => {
        try {
            const /** Checked status */ checked = event.target.checked;
            if (checked) {
                // If it's checked
                // Add the file to the selected list
                selectedFiles.push(file);
            }
            else {
                // Remove the file fromt he selected list
                const /** Unchecked file index */ unselectedIdx = selectedFiles.findIndex((selectedFile) => ((selectedFile === null || selectedFile === void 0 ? void 0 : selectedFile.url) === (file === null || file === void 0 ? void 0 : file.url)));
                selectedFiles.splice(unselectedIdx, 1);
            }
            // Update selected files
            setSelectedFiles([...selectedFiles]);
            return true;
        }
        catch (err) {
            // Log error
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `InfobaseTableSG, handleCheckboxChange, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Notify to user
            toast.error(t('Something went wrong. We are figuring it out.'));
            return false;
        }
        ;
    };
    const [/** Total files */ total, setTotal] = useState(0);
    const [/** Table page from 0 */ page, setPage] = useState(0);
    const [/** Table items/page */ limit, setLimit] = useState(10);
    /**
     * Handle page change
     * @param event Page chang event
     * @param value page number, from 0
     * @returns success/fail
     */
    const handlePageChange = (event, value) => {
        // Update page
        setPage(value);
        return true;
    };
    /**
     * Handle limit change
     * @param event Limit change event
     * @returns boolean of success/fail
     */
    const handleLimitChange = (event) => {
        // Update items/page
        setLimit(event.target.value);
        return true;
    };
    let /** Local date format */ dateFormat = 'DD MMM YYYY';
    switch (i18n.language) {
        case MULTILANGUAGE.en.value:
            // English
            dateFormat = 'DD MMM YYYY';
            break;
        case MULTILANGUAGE.ja.value:
            // Japanese
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.ko.value:
            // Korean
            dateFormat = 'YYYY년 M월 D일';
            break;
        case MULTILANGUAGE['zh-CN'].value:
            // Simplified Chinese
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE['zh-HK'].value:
            // Tranditional Chinese
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.id.value:
            // Indonesian
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.th.value:
            // Thai
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.vi.value:
            // Vietnamese
            dateFormat = 'DD MMMM YYYY';
            break;
    }
    const [/** Infobase files list */ files, setFiles] = useState([]);
    // Fetch and update Infobase files list when user credential loaded
    useEffect(() => {
        /** Get Infobase data */
        const getInfobase = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                // Set headers
                const /** Auth headers */ authHeader = yield getAuthHeader(user);
                const /** Request headers */ headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Call Get infobase API
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.INFOBASE}${ENDPOINTS.GET_INFOBASE}?page=${page}&limit=${limit}`, { headers });
                const { /** Files list */ files, /** Total files */ total } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                // Update files
                setFiles([...files]);
                // Update total files
                setTotal(total);
                return true;
            }
            catch (err) {
                // Log error
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `InfobaseTableSG, getInfobase, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.AI_CHAT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator.userAgent,
                    },
                });
                return false;
            }
        });
        try {
            if (user) {
                // If use credential is loaded
                getInfobase();
            }
        }
        catch (err) {
            // Log error
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `InfobaseTableSG, useEffect, getInfobase, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
    }, [user, page, limit, reloadInfobaseFilesFlag]);
    return (_jsxs(Box, Object.assign({ sx: {
            width: 'auto',
        } }, { children: [_jsx(Box, { children: _jsx(TableContainer, Object.assign({ sx: { padding: '0' } }, { children: _jsxs(Table, Object.assign({ "aria-label": "table" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, Object.assign({ sx: {
                                                padding: '0.875rem 1rem',
                                                width: '52%',
                                            } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: infobaseTableSGStyles.tableCell }, { children: t('NAME') })) })), _jsx(TableCell, Object.assign({ sx: {
                                                padding: '0.875rem 1rem',
                                                width: '25%',
                                            } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: infobaseTableSGStyles.tableCell }, { children: t('TYPE') })) })), _jsx(TableCell, Object.assign({ sx: {
                                                padding: '0.875rem 1rem',
                                            } }, { children: _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center' } }, { children: [_jsx(Typography, Object.assign({ variant: 'text14', sx: infobaseTableSGStyles.tableCell }, { children: t('CREATED AT') })), _jsx(ArrowDropDown, { sx: { color: 'blackDark.main' } })] })) }))] }) }), _jsx(TableBody, { children: files.map((file) => {
                                    const fileName = file === null || file === void 0 ? void 0 : file.name;
                                    const fileType = ((fileName || '').split('.').pop() || '').toLowerCase();
                                    const isDisabled = attachFileTypes.findIndex((attachFileType) => (attachFileType === fileType)) < 0;
                                    return (_jsx(Tooltip, Object.assign({ title: t('The selected model does not support this file type'), placement: 'top', slotProps: {
                                            tooltip: {
                                                sx: Object.assign(Object.assign({}, infobaseTableSGStyles.tooltip), { display: isDisabled ? 'block' : 'none' })
                                            }
                                        } }, { children: _jsxs(TableRow, Object.assign({ hover: true, sx: { cursor: 'pointer' } }, { children: [_jsx(TableCell, Object.assign({ sx: { padding: '0 0 0 1rem' } }, { children: _jsxs(Grid, Object.assign({ container: true, columnSpacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Checkbox, { sx: { padding: '0', color: 'blackDark.main' }, checked: selectedFiles.findIndex(((selectedFile) => ((selectedFile === null || selectedFile === void 0 ? void 0 : selectedFile._id) === (file === null || file === void 0 ? void 0 : file._id)))) >= 0, onClick: (event) => {
                                                                        event.stopPropagation();
                                                                        handleCheckboxChange(event, file);
                                                                    }, disabled: isDisabled }) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    maxWidth: 'calc(100%)',
                                                                    width: '1px',
                                                                    overflow: 'hidden',
                                                                } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: Object.assign(Object.assign({}, infobaseTableSGStyles.tableCell), { textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }, { children: fileName })) }))] })) })), _jsx(TableCell, Object.assign({ sx: { padding: '0 0 0 1rem' } }, { children: _jsx(Grid, Object.assign({ container: true, sx: { alignItems: 'center' }, columnSpacing: 1 }, { children: _jsx(Grid, Object.assign({ item: true, xs: true, sx: { display: 'flex' } }, { children: _jsx(Grid, Object.assign({ container: true }, { children: _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex' } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: Object.assign(Object.assign({}, infobaseTableSGStyles.tableCell), { textAlign: 'left' }) }, { children: fileType.toUpperCase() })) })) })) })) })) })), _jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, infobaseTableSGStyles), { padding: '0.875rem 1rem' }) }, { children: _jsx(Box, Object.assign({ sx: { overflow: 'hidden', display: 'flex', width: '100%' } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: {
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            } }, { children: moment.utc(file === null || file === void 0 ? void 0 : file.createdAt).local().locale(i18n.language).format(dateFormat) })) })) }))] }), file === null || file === void 0 ? void 0 : file._id) })));
                                }) })] })) })) }), _jsx(Box, Object.assign({ sx: {
                    width: '100%',
                    textAlign: 'right',
                    justifyContent: 'flex-end',
                } }, { children: _jsx(TablePagination, { component: "div", count: total, page: page, onPageChange: handlePageChange, rowsPerPage: limit, onRowsPerPageChange: handleLimitChange, labelRowsPerPage: t('Results'), labelDisplayedRows: ({ from, to, count }) => (`${from}–${to} ${t('of')} ${count !== -1 ? count : `more than ${to}`}`) }) }))] })));
}
